import { useTheme } from "@mui/material";
import { PlanChoiceStatsEntry } from "api/admin";
import usePlanChoiceStats from "components/hooks/usePlanChoiceStats";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";

const USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const formatTerms = (plan: PlanChoiceStatsEntry) => {
  const terms = [
    `${plan.revSharePercentage}% rev share`,
    `${USD.format(plan.price)} price`,
  ];
  if (plan.upfrontAmount) {
    terms.unshift(`${USD.format(plan.upfrontAmount)} upfront`);
  }
  return terms.join(" · ");
};

function PlanChoicesWidget() {
  const theme = useTheme();
  const { data, isLoading, isError } = usePlanChoiceStats();

  return (
    <DSCard
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: "20px",
      }}
    >
      <DSBox sx={{ px: 2, pt: 2 }}>
        <DSTypography variant="h6" sx={{ m: 0 }}>
          Artist Plan Choices
        </DSTypography>
        {data && (
          <DSTypography variant="body2" sx={{ color: "text.secondary", m: 0 }}>
            {data.totalArtists.toLocaleString()} artists
          </DSTypography>
        )}
      </DSBox>
      <DSCardContent sx={{ flex: 1 }}>
        {isLoading ? (
          <DSBox sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <DSCircularProgress />
          </DSBox>
        ) : isError || !data ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            Failed to load plan choices
          </DSTypography>
        ) : !data.plans.length ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            No artist has chosen a plan yet
          </DSTypography>
        ) : (
          data.plans.map((plan) => (
            <DSBox key={plan.planId} sx={{ mb: 1.5 }}>
              <DSBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  fontSize: 13,
                }}
              >
                <span>{plan.title}</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  <strong>{plan.artists.toLocaleString()}</strong> artists (
                  {plan.percentage}%)
                </span>
              </DSBox>
              <DSBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 1,
                  fontSize: 11,
                  color: "text.secondary",
                  mb: "3px",
                }}
              >
                <span>{formatTerms(plan)}</span>
                <span style={{ whiteSpace: "nowrap" }}>
                  {plan.collections.toLocaleString()} collections
                </span>
              </DSBox>
              <DSBox
                sx={{
                  height: "8px",
                  backgroundColor: "white.12",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <DSBox
                  sx={{
                    height: "100%",
                    width: `${Math.max(plan.percentage, 1)}%`,
                    backgroundColor: theme.palette.primary.main,
                    borderRadius: "4px",
                  }}
                />
              </DSBox>
            </DSBox>
          ))
        )}
      </DSCardContent>
      <DSBox
        sx={{ borderTop: `1px solid ${theme.palette.divider}`, px: 2, py: 1.5 }}
      >
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          Active plans · marketplace collections · an artist on several plans
          counts in each
        </DSTypography>
      </DSBox>
    </DSCard>
  );
}

export default PlanChoicesWidget;
